import { apiClient } from "@/api/apiClient";
import { APP_CONFIG } from "@/config/app.config";
import { useSettingsStore } from "@/store/settings";
import type { TaxCalculationLine, SubmitTsdRequest, SubmitTsdResult, TsdSubmissionRequestDto, TsdSubmissionDetailDto, PollTsdStatusResult, TsdValidationErrorDto } from "./types";

export async function getTaxCalculations(
    companyId: number,
    payoutMonth6: string
): Promise<TaxCalculationLine[]> {
    return apiClient.fetch<TaxCalculationLine[]>(
        `companies/${companyId}/tsd-submissions/tax-calculations?payoutMonth6=${encodeURIComponent(payoutMonth6)}`
    );
}

export async function submitTsd(
    companyId: number,
    request: SubmitTsdRequest
): Promise<SubmitTsdResult> {
    return apiClient.fetch<SubmitTsdResult>(
        `companies/${companyId}/tsd-submissions/submit`,
        {
            method: "POST",
            body: JSON.stringify(request),
        }
    );
}

export async function getSubmissions(
    companyId: number,
    payoutMonth6: string
): Promise<TsdSubmissionRequestDto[]> {
    return apiClient.fetch<TsdSubmissionRequestDto[]>(
        `companies/${companyId}/tsd-submissions?payoutMonth6=${encodeURIComponent(payoutMonth6)}`
    );
}

export async function getSubmissionDetail(
    companyId: number,
    submissionId: string
): Promise<TsdSubmissionDetailDto> {
    return apiClient.fetch<TsdSubmissionDetailDto>(
        `companies/${companyId}/tsd-submissions/${submissionId}`
    );
}

export async function pollStatus(
    companyId: number,
    submissionId: string
): Promise<PollTsdStatusResult> {
    return apiClient.fetch<PollTsdStatusResult>(
        `companies/${companyId}/tsd-submissions/${submissionId}/poll`,
        {
            method: "POST",
        }
    );
}

export async function recalculateTaxes(
    companyId: number,
    payoutMonth6: string
): Promise<void> {
    await apiClient.fetch<void>(
        `companies/${companyId}/tsd-submissions/recalculate?payoutMonth6=${encodeURIComponent(payoutMonth6)}`,
        { method: "POST" }
    );
}

export async function validateTsd(
    companyId: number,
    request: SubmitTsdRequest
): Promise<TsdValidationErrorDto[]> {
    return apiClient.fetch<TsdValidationErrorDto[]>(
        `companies/${companyId}/tsd-submissions/validate`,
        {
            method: "POST",
            body: JSON.stringify(request),
        }
    );
}

export async function downloadXbrl(
    companyId: number,
    request: SubmitTsdRequest
): Promise<void> {
    const settings = useSettingsStore();
    const baseUrl = APP_CONFIG.API_ENDPOINT;
    const fullUrl = new URL(
        `/api/app/companies/${companyId}/tsd-submissions/download-xbrl`,
        baseUrl instanceof URL ? baseUrl.href : baseUrl
    ).href;

    const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept-Language": settings.cultureInfo,
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP Error: ${response.status}`);
    }

    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition");
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] ?? `tsd-${request.payoutMonth6}.xml`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
